import { ListItemText, SvgIcon } from "@mui/material";
import { getDurationAriaLabelFromMs } from "../../../../../helpers/time";
import i18n from "../../../../../i18n";
import { forwardRef, ReactNode, useRef, useState } from "react";
import { CoreIconButton, CoreMenuButton, CoreMenuItem } from "@verity/components/core";
import styles from "./VideoControls.module.css";
import LowVolumeIcon from "../../../Resources/speaker-simple-low.svg?react";
import HighVolumeIcon from "../../../Resources/speaker-simple-high.svg?react";
import MutedIcon from "../../../Resources/muted-icon.svg?react";
import PlayArrowRoundedIcon from "../../../Resources/play.svg?react";
import PauseRoundedIcon from "@mui/icons-material/PauseRounded";
import { addFullScreenClass } from "./videoControlHelper";
import FullscreenRoundedIcon from "../../../Resources/fullScreen.svg?react";
import ExitFullscreenIcon from "../../../Resources/exitFullscreen.svg?react";
import Action from "../images/threedot.svg?react";
import DownloadRoundedIcon from "../../../Resources/download.svg?react";

interface VideoControlButtonProps extends React.HTMLAttributes<HTMLButtonElement> {
	children: ReactNode;
	className?: string;
	"aria-label": string;
	onClick?: React.MouseEventHandler<HTMLElement>;
}

export const VideoControlButton = forwardRef<HTMLButtonElement, VideoControlButtonProps>(
	function VideoControlButton(props, ref) {
		const { children, className, onClick, "aria-label": ariaLabel, ...rest } = props;
		return (
			<CoreIconButton
				className={className}
				onClick={onClick}
				aria-label={ariaLabel}
				ref={ref}
				variant="plain"
				sx={{
					"&:active": {
						backgroundColor: "#333333",
					},
					"&:hover": {
						backgroundColor: "#484848 !important", // Override corebuttonicon hover effect
					},
				}}
				{...rest}
			>
				{children}
			</CoreIconButton>
		);
	},
);

interface VolumeControlsProps {
	inFullscreen: boolean;
	onVolumeButtonClick?: () => void;
	volume: number;
	onVolumeInputChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
	showVolumeInput: boolean;
}

export function VolumeControls({
	inFullscreen,
	onVolumeButtonClick,
	volume,
	onVolumeInputChange,
	showVolumeInput,
}: VolumeControlsProps) {
	// Handles the display of the volume bar
	const [volumeInputShowing, setVolumeInputShowing] = useState(false);

	// Delay closing the volume slider on blur to allow focus to move
	// to the slider input (e.g. when tabbing from the volume button).
	// Without the timeout, the blur fires before the next focus,
	// causing the slider to hide too soon.
	const volumeBlurTimeoutRef = useRef<number | null>(null);

	const handleFocusWithin = () => {
		if (volumeBlurTimeoutRef.current !== null) {
			clearTimeout(volumeBlurTimeoutRef.current);
			volumeBlurTimeoutRef.current = null;
		}

		setVolumeInputShowing(true);
	};
	const handleBlurWithin = () => {
		volumeBlurTimeoutRef.current = window.setTimeout(() => {
			setVolumeInputShowing(false);
		}, 100);
	};

	const volumeIconSx = {
		color: "white",
		fontSize: inFullscreen ? "32px" : "22px",
		fill: "none",
	};

	const volumeSliderColorAmount = volume * 100;
	const sliderTrackStyle = {
		background:
			"linear-gradient(to right, #fff 0%, #fff " +
			volumeSliderColorAmount.toString() +
			"%, #676767 " +
			volumeSliderColorAmount.toString() +
			"%, #676767 100%)",
	};

	return (
		<div className={addFullScreenClass(styles.volumeWrap, inFullscreen)} style={{ flex: volumeInputShowing ? 1 : 0 }}>
			{showVolumeInput && (
				<div
					className={addFullScreenClass(styles.volumeHover, inFullscreen)}
					onMouseEnter={() => setVolumeInputShowing(true)}
					onMouseLeave={() => setVolumeInputShowing(false)}
					onFocus={handleFocusWithin}
					onBlur={handleBlurWithin}
				>
					<VideoControlButton
						onClick={onVolumeButtonClick}
						aria-label={
							volume === 0
								? i18n.t("result.components.studentSelection.videoPlayer.videoPlayerTSX.ariaLabels.unmute")
								: i18n.t("result.components.studentSelection.videoPlayer.videoPlayerTSX.ariaLabels.mute")
						}
						aria-haspopup="true"
					>
						<SvgIcon
							component={volume >= 0.5 ? HighVolumeIcon : volume === 0 ? MutedIcon : LowVolumeIcon}
							sx={volumeIconSx}
						/>
					</VideoControlButton>

					<div
						className={addFullScreenClass(styles.volumeBarWrapper, inFullscreen)}
						style={{
							overflow: volumeInputShowing ? "visible" : "hidden",
							visibility: volumeInputShowing ? "visible" : "hidden",
						}}
					>
						<input
							aria-label={i18n.t("result.components.studentSelection.videoPlayer.videoPlayerTSX.ariaLabels.volume")}
							className={addFullScreenClass(styles.volumeBar, inFullscreen)}
							type="range"
							value={volume}
							tabIndex={volumeInputShowing ? 0 : -1}
							min={0}
							max={1}
							step={0.01}
							style={sliderTrackStyle}
							onChange={onVolumeInputChange}
						/>
					</div>
				</div>
			)}
		</div>
	);
}

interface SeekbarProps {
	inFullscreen: boolean;
	curSeekbarValue: number;
	maxSeekbarValue: number | undefined;
	minSeekbarValue: number;
	onSeekbarChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
	children?: React.ReactNode;
	timeOffset?: number;
}

export function Seekbar({
	inFullscreen,
	curSeekbarValue,
	maxSeekbarValue,
	minSeekbarValue,
	onSeekbarChange,
	timeOffset = 0,
	children,
}: SeekbarProps) {
	const denominator = (maxSeekbarValue ?? 0) - timeOffset;
	const colorAmount = denominator > 0 ? ((curSeekbarValue - timeOffset) / denominator) * 100 : 0;
	const seekbarSliderColor = {
		background:
			"linear-gradient(to right, #fff 0%, #fff " +
			colorAmount.toString() +
			"%, #676767 " +
			colorAmount.toString() +
			"%, #676767 100%)",
	};

	return (
		<div className={addFullScreenClass(styles.seekbarContainer, inFullscreen)}>
			<div className={addFullScreenClass(styles.seekBarWrap, inFullscreen)}>
				<input
					className={addFullScreenClass(styles.seekbar, inFullscreen)}
					id="seekbar_Video"
					type="range"
					value={curSeekbarValue}
					style={seekbarSliderColor}
					min={minSeekbarValue}
					max={maxSeekbarValue}
					aria-label={i18n.t("result.components.studentSelection.videoPlayer.videoPlayerTSX.ariaLabels.seekSlider")}
					aria-valuemin={0} // For screen readers, tell user that video starts at 0 seconds
					aria-valuemax={maxSeekbarValue ? Math.round(maxSeekbarValue / 1000) : 0} // Convert to second
					aria-valuenow={Math.round(curSeekbarValue / 1000)}
					aria-valuetext={getDurationAriaLabelFromMs(curSeekbarValue)}
					onChange={onSeekbarChange}
				/>
				{children}
			</div>
		</div>
	);
}

type PlayButtonProps = {
	isPlaying: boolean;
	onClick: () => void;
	inFullscreen: boolean;
};

export function PlayButton({ isPlaying, onClick, inFullscreen }: PlayButtonProps) {
	return (
		<VideoControlButton
			aria-label={
				isPlaying
					? i18n.t("result.components.studentSelection.videoPlayer.videoPlayerTSX.pauseVideoLabel")
					: i18n.t("result.components.studentSelection.videoPlayer.videoPlayerTSX.playVideoLabel")
			}
			onClick={onClick}
			className={addFullScreenClass(styles.playButton, inFullscreen)}
		>
			<SvgIcon
				className={addFullScreenClass(styles.controlIcon, inFullscreen)}
				component={isPlaying ? PauseRoundedIcon : PlayArrowRoundedIcon}
			/>
		</VideoControlButton>
	);
}

type TimeStampProps = {
	children: ReactNode;
	inFullscreen: boolean;
};

export function VideoTimeStamp({ children, inFullscreen }: TimeStampProps) {
	return <span className={addFullScreenClass(styles.videoTime, inFullscreen)}>{children}</span>;
}

type VideoSpeedButtonProps = {
	inFullscreen: boolean;
	menuShowing: boolean;
	setMenuShowing: (showing: boolean) => void;
	playbackSpeed: number;
	controlContainerRef: React.RefObject<HTMLDivElement>;
	onSelectSpeed: (speed: number) => void;
};

export function VideoSpeedButton({
	inFullscreen,
	menuShowing,
	setMenuShowing,
	playbackSpeed,
	controlContainerRef,
	onSelectSpeed,
}: VideoSpeedButtonProps) {
	return (
		<CoreMenuButton
			className={addFullScreenClass(styles.videoSpeedButton, inFullscreen)}
			menuShowing={menuShowing}
			setMenuShowing={setMenuShowing}
			style={{ backgroundColor: "#00000000" }}
			icon={<div style={{ color: "white" }}>{playbackSpeed}x</div>}
			slotProps={{
				menu: {
					transformOrigin: {
						vertical: "bottom",
						horizontal: "center",
					},
					anchorOrigin: {
						vertical: "top",
						horizontal: "center",
					},
					container: controlContainerRef.current,
					slotProps: {
						menuList: {
							role: "listbox",
						},
					},
				},
			}}
			aria-label={i18n.t("result.components.studentSelection.videoPlayer.videoPlayerTSX.ariaLabels.playbackSpeed", {
				speed: playbackSpeed,
			})}
			aria-haspopup="listbox"
		>
			{[0.25, 0.5, 1, 1.25, 1.5, 1.75, 2, 4].map((x) => {
				const isSelected = x === playbackSpeed;
				return (
					<CoreMenuItem
						key={x}
						role="option"
						aria-selected={isSelected}
						selected={isSelected}
						onClick={(_e) => {
							onSelectSpeed(x);
							setMenuShowing(false);
						}}
					>
						<ListItemText primary={`${x}x`} />
					</CoreMenuItem>
				);
			})}
		</CoreMenuButton>
	);
}

type FullScreenButtonProps = {
	inFullscreen: boolean;
	onClick: () => void;
};

export function FullScreenButton({ inFullscreen, onClick }: FullScreenButtonProps) {
	return (
		<div className={addFullScreenClass(styles.fullscreenButtonWrapper, inFullscreen)}>
			<VideoControlButton
				className={addFullScreenClass(styles.fullScreenButton, inFullscreen)}
				onClick={onClick}
				aria-label={
					inFullscreen
						? i18n.t("result.components.studentSelection.videoPlayer.videoPlayerTSX.exitFullscreen")
						: i18n.t("result.components.studentSelection.videoPlayer.videoPlayerTSX.enterFullscreen")
				}
			>
				<SvgIcon
					className={addFullScreenClass(styles.controlIcon, inFullscreen)}
					component={inFullscreen ? ExitFullscreenIcon : FullscreenRoundedIcon}
				/>
			</VideoControlButton>
		</div>
	);
}

type MoreOptionsButtonProps = {
	menuShowing: boolean;
	setMenuShowing: (showing: boolean) => void;
	inFullscreen: boolean;
	onDeleteRecording: () => void;
};

export function MoreOptionsButton({
	menuShowing,
	setMenuShowing,
	inFullscreen,
	onDeleteRecording,
}: MoreOptionsButtonProps) {
	return (
		<CoreMenuButton
			aria-label={i18n.t("result.components.studentSelection.videoPlayer.videoPlayerTSX.ariaLabels.moreOptions")}
			aria-haspopup="true"
			className={styles.moreOptionsButton}
			menuShowing={menuShowing}
			setMenuShowing={setMenuShowing}
			slotProps={{
				menu: {
					transformOrigin: {
						vertical: "bottom",
						horizontal: "center",
					},
					anchorOrigin: {
						vertical: "top",
						horizontal: "center",
					},
				},
			}}
			style={{ backgroundColor: "#17171D" }}
			icon={<SvgIcon className={addFullScreenClass(styles.controlIcon, inFullscreen)} component={Action} />}
		>
			<CoreMenuItem
				onClick={() => {
					onDeleteRecording();
					setMenuShowing(false);
				}}
			>
				<ListItemText primary={i18n.t("result.components.resultsPage.proctoringResultsViewTSX.deleteRecording")} />
			</CoreMenuItem>
		</CoreMenuButton>
	);
}

type DownloadButtonProps = {
	onClick: () => void;
	inFullscreen: boolean;
};

export function DownloadButton({ onClick, inFullscreen }: DownloadButtonProps) {
	return (
		<VideoControlButton
			className={addFullScreenClass(styles.downloadButton, inFullscreen)}
			aria-label={i18n.t("result.components.studentSelection.videoPlayer.videoPlayerTSX.downloadVideo")}
			aria-haspopup="true"
			onClick={onClick}
		>
			<SvgIcon className={addFullScreenClass(styles.controlIcon, inFullscreen)} component={DownloadRoundedIcon} />
		</VideoControlButton>
	);
}
